<template>
  <div class="partners">
    <div class="container tile mt-4 pb-4 mb-8">
      <loading-view :loading="loadingCompany">
        <div class="form form_justify_left py-4">
          <h3 class="h4">{{ trans('__JSON__.Partner Profile') }}</h3>
          <form role="form" class="pt-4" @submit.prevent="store">
            <!-- Logo -->
            <div class="form-group row">
              <div class="col-md-6 d-flex align-items-center">
                <div class="image-placeholder mr-4">
                  <span role="img" class="profile-photo-preview" :style="previewStyle"></span>
                </div>
                <div class="spark-uploader">
                  <input
                    ref="logo"
                    type="file"
                    class="spark-uploader-control"
                    name="photo"
                    accept="image/*"
                    @change="uploadLogo"
                    :disabled="form.busy"
                  />
                  <div class="button button_hollow">
                    <span v-if="this.uploadingLogo"><i class="fa fa-btn fa-spinner fa-spin"></i> Uploading</span>
                    <span v-else>Upload Logo</span>
                  </div>
                </div>
              </div>
            </div>

            <form-input
              type="text"
              id="partner-name"
              name="company_name"
              v-model="form.company_name"
              container-class="form-group"
              :label-text="trans('common.company_name')"
              :placeholder="trans('common.company_name')"
              :error="form.errors.company_name"
              required
              autofocus
            />

            <form-translatable-input
              type="textarea"
              id="partner-description"
              name="description"
              v-model="form.description"
              container-class="form-group"
              :label-text="trans('__JSON__.Description') + ' *'"
              :placeholder="trans('__JSON__.Description')"
              :errors="form.errors"
              error-key="description"
            />

            <form-input
              type="multi-select"
              id="partner-applications"
              name="applications"
              v-model="form.applications"
              :options="applications"
              container-class="form-group"
              label="name"
              track-by="id"
              :label-text="trans('common.applications')"
              :placeholder="trans('common.applications')"
              required
              :error="form.errors.applications"
            />

            <form-input
              type="multi-select"
              id="partner-languages"
              name="language"
              v-model="form.language"
              :options="languages"
              container-class="form-group"
              label="name"
              track-by="id"
              :label-text="trans('__JSON__.Contact Language')"
              :placeholder="trans('__JSON__.Contact Language')"
              :error="form.errors.language"
              required
            />

            <form-input
              type="multi-select"
              id="partner-branches"
              name="branches"
              v-model="form.branches"
              :options="branches"
              container-class="form-group"
              label="name"
              track-by="id"
              :label-text="trans('__JSON__.Branches')"
              :placeholder="trans('__JSON__.Branches')"
              :error="form.errors.branches"
            />

            <form-input
              type="multi-select"
              id="partner-focus-areas"
              name="focus_areas"
              v-model="form.focus_areas"
              :options="focusAreas"
              container-class="form-group"
              label="name"
              track-by="id"
              :label-text="trans('__JSON__.Focus Areas')"
              :placeholder="trans('__JSON__.Focus Areas')"
              :error="form.errors.focus_areas"
            />

            <form-input
              type="select"
              id="partner-country"
              name="applications"
              v-model="form.country"
              :options="countries"
              container-class="form-group"
              label="name"
              track-by="code"
              :label-text="trans('common.country')"
              :placeholder="trans('common.country')"
              :error="form.errors.country"
            />

            <form-input
              type="email"
              id="contract-support-email"
              name="contact_support_email"
              v-model="form.contact_support_email"
              container-class="form-group"
              :label-text="trans('__JSON__.Contact Support Email')"
              :placeholder="trans('__JSON__.Contact Support Email')"
              :error="form.errors.contact_support_email"
              required
            />

            <form-input
              type="text"
              id="partner-website"
              name="website"
              v-model="form.website"
              container-class="form-group"
              :label-text="trans('__JSON__.Website')"
              :placeholder="trans('__JSON__.Website')"
              :error="form.errors.website"
            />

            <form-input
              type="text"
              id="partner-intake_meeting_link"
              name="intake_meeting_link"
              v-model="form.intake_meeting_link"
              container-class="form-group"
              :label-text="trans('__JSON__.Intake Meeting Link')"
              :placeholder="trans('__JSON__.Intake Meeting Link')"
              :error="form.errors.intake_meeting_link"
            />

            <partner-package-form
              :errors="form.errors"
              :packages="form.packages"
              @update:packages="form.packages = $event"
            ></partner-package-form>

            <div>
              <hr>
              <form-input-label :value="trans('__JSON__.Certificates')"/>
              <form-dropzone
                :placeholder="trans('__JSON__.Drag and drop the certificates here, or click to select certificates')"
                :multiple="5"
                accept="image/*"
                @onDrop="handleOnDropCertificates"
              />

              <div v-if="certificates.length > 0">
                <form-input-label :value="trans('__JSON__.Existing Certificates')"/>
                <div class="flex flex-wrap gap">
                  <div v-for="certificate in certificates" :key="certificate.id" class="w-1/4">
                    <div class="flex flex-col items-center">
                      <img
                        :src="certificate.certificate_s3_url"
                        class="w-32 h-32 object-cover"
                        :alt="`certificate-image-${certificate.id}`"
                      />
                      <button
                        class="button button_danger mt-2"
                        type="button"
                        @click="removeCertificate(certificate.id)"
                      >
                        {{ trans('__JSON__.Remove') }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <hr>

              <form-input-label :value="trans('__JSON__.Terms And Conditions')"/>
              <form-dropzone
                v-if="! company.terms_and_conditions_url"
                :placeholder="trans('__JSON__.Drag and drop the terms and conditions here, or click to select terms and conditions')"
                :maxFiles="1"
                accept="application/pdf"
                @onDrop="handleOnDropTerms"
              />

              <div v-if="company.terms_and_conditions_url">
                <div class="flex flex-wrap gap">
                  <div class="flex flex-col">
                    <a
                      :href="company.terms_and_conditions_url"
                      class="w-32 h-32 object-cover"
                      target="_blank"
                    > {{ trans('__JSON__.View PDF') }}
                    </a>
                    <button
                      class="button button_danger mt-2"
                      type="button"
                      @click="removeTermsAndConditions()"
                    >
                      {{ trans('__JSON__.Remove PDF') }}
                    </button>
                  </div>
                </div>
              </div>
              <hr>

              <div v-if="company.contract_url" class="my-3">
                <form-input-label :value="trans('__JSON__.Contract')"/>
                <div class="flex flex-col items-start">
                  <a :href="company.contract_url" target="_blank" class="button button_primary">
                    {{ trans('__JSON__.View Signed Contract') }}
                  </a>
                </div>
              </div>
            </div>

            <div class="form_row">
              <span></span>
              <div>
                <inertia-link
                  :href="$route('dashboard.partner.customers.show')"
                  class="button button_cancel"
                  :disabled="form.processing"
                >
                  <span>{{ trans('common.cancel') }}</span>
                </inertia-link>

                <form-button
                  :value="trans('common.save')"
                  :processing-value="trans('__JSON__.Saving')"
                  :processing="form.processing"
                />
              </div>
            </div>
          </form>
        </div>
      </loading-view>
    </div>
    <div class="container tile mt-4 pb-4 mb-8">
      <loading-view :loading="loadingCompany">
        <h3 class="h4">{{ trans('__JSON__.Partner Info') }}</h3>
        <div class="my-3">
          <p>{{ trans('__JSON__.Partner Terms and Conditions Status') }}: </p>
          <p v-if="this.company.terms_status != null" class="font-bold">
            <span :class="{ 'text-danger': !this.company.terms_status, 'text-success': this.company.terms_status }">
              {{ this.company.terms_status ? trans('__JSON__.Approved') : trans('__JSON__.Denied') }}
            </span>
            <span> - {{ this.company.terms_status_updated_by }}</span>
          </p>
          <p v-else>{{ trans('__JSON__.Pending') }}</p>
          <hr>
          <p>{{ trans('__JSON__.Commission on Monthly Recurring Revenue') }}: {{ this.signedPartnerContract.commission_on_mrr ?? 0 }}%</p>
          <p>{{ trans('__JSON__.Commission on Consultancy') }}: {{ this.signedPartnerContract.commission_on_consultancy ?? 0 }}%</p>
          <a v-if="signedPartnerContract" :href="route('partner.contract.show')" target="_blank">{{ trans('__JSON__.Show contract') }}</a>
        </div>
      </loading-view>
    </div>
    <div v-if="company.is_regional_partner === true" class="container tile mt-4 pb-4 mb-8">
      <loading-view :loading="loadingCompany">
        <h3 class="h4">{{ trans('__JSON__.Partner Services') }}</h3>
        <form role="form" class="form form_justify_left" @submit.prevent="storeServices">
          <form-input
            type="text"
            id="hourly-rate"
            name="hourly_rate"
            v-model="formServices.hourly_rate"
            container-class="form-group"
            :label-text="trans('__JSON__.Hourly Rate')"
            :placeholder="trans('__JSON__.Hourly Rate')"
            :error="formServices.errors.hourly_rate"
            required
            autofocus
          />
          <hr>
          <div v-for="(service) in formServices.services" :key="`service-${service.value}`">
            <form-input
              :key="`service-${service.value}`"
              type="integer"
              v-model="service.price"
              container-class="form-group"
              :label-text="service.title"
              :placeholder="service.title"
              :error="formServices.errors.hourly_rate"
              required
              autofocus
            />
          </div>
          <div class="form_row">
            <span></span>
            <form-button
              :value="trans('common.save')"
              :processing-value="trans('__JSON__.Saving')"
              :processing="formServices.processing"
            />
          </div>
        </form>
      </loading-view>
    </div>
  </div>
</template>

<script>
import {useForm} from '@inertiajs/vue3';
import Swal from 'sweetalert2';
import DashboardLayout from '@/Layouts/DashboardLayout';
import Multiselect from 'vue-multiselect';
import FormInput from '@/components/Shared/Form/Input/Index';
import FormTranslatableInput from '@/components/Shared/Form/Input/Translatable.vue';
import FormInputLabel from '@/components/Shared/Form/Input/Label';
import FormButton from '@/components/Shared/Form/Button';
import FormDropzone from '@/components/Shared/Form/Dropzone/Index';
import PartnerPackageForm from '@/Pages/Dashboard/Partner/Partials/PartnerPackageForm.vue';

export default {
  name: 'CompanyProfile',

  layout: DashboardLayout,

  props: {
    focusAreas: {
      type: Array,
    },
    branches: {
      type: Array,
    },
    languages: {
      type: Array,
    },
  },

  components: {
    PartnerPackageForm,
    FormButton,
    FormInput,
    FormTranslatableInput,
    FormInputLabel,
    Multiselect,
    FormDropzone,
  },

  data() {
    return {
      loadingCompany: true,
      uploadingLogo: false,

      company: {},
      signedPartnerContract: {},

      form: useForm({
        company_name: '',
        description: '',
        applications: [],
        focus_areas: [],
        branches: [],
        language: [],
        country: '',
        website: '',
        intake_meeting_link: '',
        certificates: [],
        contact_support_email: '',
        packages: [],
        terms: null,
      }),

      formServices: useForm({
        hourly_rate: '',
        services: [],
      }),

      previewLogo: null,

      applications: [],

      countries: [],

      certificates: [],
    };
  },

  async beforeMount() {
    await this.initializeData();

    this.loadingCompany = false;
  },

  computed: {
    /**
     * Calculate the style attribute for the photo preview.
     */
    previewStyle() {
      return `background-image: url(${this.previewLogo})`;
    },
  },

  methods: {
    async initializeData() {
      await axios.get(route('partner.company.index')).then(response => {
        this.company = response.data.partner;
        this.signedPartnerContract = response.data.signedPartnerContract;
        this.applications = response.data.applications;
        this.countries = response.data.countries;
        this.certificates = response.data.certificates || [];
        this.previewLogo = this.company.logo_s3_url;

        this.form.company_name = this.company.company_name || '';
        this.form.description = this.company.description || '';
        this.form.website = this.company.website || '';
        this.form.intake_meeting_link = this.company.intake_meeting_link || '';
        this.form.contact_support_email = this.company.contact_support_email || '';
        this.form.applications = this.company.applications || [];
        this.form.language = this.company.language || [];
        this.form.focus_areas = this.company.focus_areas || [];
        this.form.branches = this.company.branches || [];
        this.form.country = this.company.country
          ? this.countries.find(country => country.code === this.company.country)
          : '';
        this.form.packages = this.company.packages || [];
      });

      await axios.get(route('partner.company.services')).then(response => {
        this.formServices.hourly_rate = response.data.pricePerHour;
        this.formServices.services = response.data.gettingStartedServices;
      });
    },

    /**
     * Update the user's profile photo.
     */
    uploadLogo() {
      let self = this;
      let formData = new FormData();
      let file = this.$refs.logo.files[0];

      formData.append('logo', file);

      this.uploadingLogo = true;

      axios
        .post(route('partner.company.logo.update'), formData)
        .then(response => {
          self.previewLogo = response.data;
        })
        .catch(error => {
          self.form.setErrors(error.response.data.errors);
        })
        .finally(() => {
          self.uploadingLogo = false;
          self.$refs.logo.value = null;
        });
    },

    store() {
      this.form
        .transform(data => ({
          ...data,
          _method: 'put',
          applications: data.applications.map(application => application.id),
          focus_areas: data.focus_areas.map(focusArea => focusArea.id),
          branches: data.branches.map(branch => branch.id),
          language: data.language.map(language => language.id),
          country: data.country?.code,
        }))
        .post(route('partner.company.update'), {
          onSuccess: () => {
            Swal.fire({
              title: this.trans('__JSON__.Success'),
              text: this.trans('__JSON__.Your partner profile has been updated!'),
              icon: 'success',
              showConfirmButton: false,
              timer: 3000,
            });
          },
        });
    },

    storeServices() {
      this.formServices
        .post(route('dashboard.support.services.store'), {
          onSuccess: () => {
            this.initializeData();

            this.form.certificates = [];
            this.form.terms = null;

            Swal.fire({
              title: this.trans('__JSON__.Success'),
              text: this.trans('__JSON__.Your partner services have been updated!'),
              icon: 'success',
              showConfirmButton: false,
              timer: 3000,
            });
          },
        });
    },

    handleOnDropCertificates(acceptFiles) {
      this.form.certificates = acceptFiles;
    },

    handleOnDropTerms(acceptFiles) {
      this.form.terms = acceptFiles[0];
    },

    removeCertificate(certificateId) {
      this.certificates = this.certificates.filter(certificate => certificate.id !== certificateId);

      axios.delete(route('partner.company.certificate.delete', certificateId));
    },

    removeTermsAndConditions() {
      axios.delete(route('partner.company.terms.delete'));

      this.company.terms_and_conditions_url = null
    },
  },
};
</script>
